/**
 * Central RPC Endpoint Resolver
 * 
 * Handles RPC endpoint selection with graceful fallback.
 * Client-side code NEVER uses Helius directly - only standard public RPCs.
 * Server-side (edge functions) may use Helius with fallback to primary RPC.
 */

import { Connection, ConnectionConfig } from '@solana/web3.js';

// Environment variables (Vite style)
const DEFAULT_CLUSTER = (import.meta.env.VITE_SOLANA_CLUSTER || 'devnet') as 'mainnet-beta' | 'devnet';
const HELIUS_API_KEY = import.meta.env.VITE_HELIUS_API_KEY || '';
const RPC_PRIMARY = import.meta.env.VITE_RPC_PRIMARY || 
  (HELIUS_API_KEY && DEFAULT_CLUSTER === 'mainnet-beta' 
    ? `https://mainnet.helius-rpc.com/?api-key=${HELIUS_API_KEY}`
    : DEFAULT_CLUSTER === 'mainnet-beta' 
      ? 'https://api.mainnet-beta.solana.com'
      : 'https://api.devnet.solana.com');

// Runtime cluster (can be changed by user)
let currentCluster: 'mainnet-beta' | 'devnet' = DEFAULT_CLUSTER;

// Event listeners for cluster changes
type ClusterChangeListener = (newCluster: 'mainnet-beta' | 'devnet') => void;
const clusterChangeListeners: ClusterChangeListener[] = [];

export interface RpcOptions {
  preferHelius?: boolean;
  commitment?: 'processed' | 'confirmed' | 'finalized';
  cluster?: 'mainnet-beta' | 'devnet';
}

/**
 * Get the current cluster
 * @returns Current cluster setting
 */
export function getCurrentCluster(): 'mainnet-beta' | 'devnet' {
  return currentCluster;
}

/**
 * Set the runtime cluster (requires user confirmation in UI)
 * @param cluster - Cluster to switch to
 */
export function setRpcCluster(cluster: 'mainnet-beta' | 'devnet'): void {
  const oldCluster = currentCluster;
  currentCluster = cluster;
  console.log(`rpc.ts: 🌐 switched to ${cluster}`);
  
  // Notify all listeners
  if (oldCluster !== cluster) {
    clusterChangeListeners.forEach(listener => listener(cluster));
  }
}

/**
 * Subscribe to cluster changes
 * @param listener - Callback function to be called when cluster changes
 * @returns Unsubscribe function
 */
export function onClusterChange(listener: ClusterChangeListener): () => void {
  clusterChangeListeners.push(listener);
  return () => {
    const index = clusterChangeListeners.indexOf(listener);
    if (index > -1) {
      clusterChangeListeners.splice(index, 1);
    }
  };
}

/**
 * Get the appropriate RPC endpoint
 * @param options - Configuration options
 * @returns RPC endpoint URL
 */
export function getRpcEndpoint(options: RpcOptions = {}): string {
  const { preferHelius = false, cluster } = options;
  const targetCluster = cluster || currentCluster;
  
  // Determine base URL for the cluster
  const clusterUrl = targetCluster === 'mainnet-beta'
    ? 'https://api.mainnet-beta.solana.com'
    : 'https://api.devnet.solana.com';
  
  // Client-side: Use Helius if available for better reliability
  // Public mainnet RPC is heavily rate-limited and unreliable
  if (typeof window !== 'undefined') {
    // For mainnet, prefer Helius if API key is available
    if (HELIUS_API_KEY && targetCluster === 'mainnet-beta') {
      const heliusUrl = `https://mainnet.helius-rpc.com/?api-key=${HELIUS_API_KEY}`;
      console.log(`🌐 RPC endpoint: helius-mainnet (${targetCluster}, client, reliable)`);
      return heliusUrl;
    }
    console.log(`🌐 RPC endpoint: ${clusterUrl} (${targetCluster}, client, public)`);
    return clusterUrl;
  }
  
  // Server-side can use Helius if key exists and explicitly requested
  if (preferHelius && HELIUS_API_KEY) {
    const heliusUrl = targetCluster === 'mainnet-beta'
      ? `https://mainnet.helius-rpc.com/?api-key=${HELIUS_API_KEY}`
      : `https://devnet.helius-rpc.com/?api-key=${HELIUS_API_KEY}`;
    console.log(`🌐 RPC endpoint: helius (${targetCluster}, server, fallback=true)`);
    return heliusUrl;
  }
  
  console.log(`🌐 RPC endpoint: ${clusterUrl} (${targetCluster}, server, fallback=false)`);
  return clusterUrl;
}

/**
 * Create a Solana connection with appropriate RPC endpoint
 * @param options - Connection options
 * @returns Solana Connection instance
 */
export function getConnection(options: RpcOptions = {}): Connection {
  const endpoint = getRpcEndpoint(options);
  const commitment = options.commitment || 'confirmed';
  
  const config: ConnectionConfig = {
    commitment,
    confirmTransactionInitialTimeout: 60000, // 60 seconds
  };
  
  return new Connection(endpoint, config);
}

/**
 * Retry a function with RPC fallback
 * @param fn - Function to execute
 * @param context - Context description for logging
 * @returns Result of the function
 */
export async function withRpcFallback<T>(
  fn: (connection: Connection) => Promise<T>,
  context: string = 'RPC operation'
): Promise<T> {
  let lastError: Error | null = null;
  let attempts = 0;
  
  // Try with current connection first
  try {
    attempts++;
    const connection = getConnection();
    return await fn(connection);
  } catch (error: any) {
    lastError = error;
    
    // Check if it's an RPC error (401, 403, 429, or network error)
    const isRpcError = 
      error?.message?.includes('401') ||
      error?.message?.includes('403') ||
      error?.message?.includes('429') ||
      error?.message?.includes('Invalid API key') ||
      error?.message?.includes('Network') ||
      error?.code === 'ECONNREFUSED';
    
    if (isRpcError && attempts === 1) {
      console.warn(`⚠️ ${context} failed with RPC error, retrying with primary RPC...`, error.message);
      
      try {
        attempts++;
        // Force primary RPC (no Helius)
        const fallbackConnection = new Connection(RPC_PRIMARY, 'confirmed');
        return await fn(fallbackConnection);
      } catch (retryError: any) {
        lastError = retryError;
        console.error(`❌ ${context} failed after fallback:`, retryError);
      }
    }
  }
  
  // All attempts failed
  const userMessage = 'Network unavailable. Please try again in a moment.';
  console.error(`❌ ${context} failed after ${attempts} attempts:`, lastError);
  throw new Error(userMessage);
}

/**
 * Normalize RPC errors for user display
 * @param error - The error to normalize
 * @param context - Context for the error
 * @returns User-friendly error message
 */
export function normalizeRpcError(error: any, context: string = 'operation'): string {
  // Log detailed error for developers
  console.error(`RPC error during ${context}:`, {
    message: error?.message,
    code: error?.code,
    status: error?.status,
  });
  
  // Map specific errors to user-friendly messages
  if (error?.message?.includes('401') || error?.message?.includes('Invalid API key')) {
    return 'Temporary network issue verifying NFT ownership. Please try again.';
  }
  
  if (error?.message?.includes('403')) {
    return 'Temporary network issue verifying NFT ownership. Please try again.';
  }
  
  if (error?.message?.includes('429')) {
    return 'Network is busy. Please wait a moment and try again.';
  }
  
  if (error?.message?.includes('Network') || error?.code === 'ECONNREFUSED') {
    return 'Network connection failed. Please check your internet and try again.';
  }
  
  // Default message
  return `${context} failed. Please try again.`;
}

/**
 * Check if Helius is available (server-side only)
 * @returns True if Helius API key is configured
 */
export function isHeliusAvailable(): boolean {
  return Boolean(HELIUS_API_KEY && typeof window === 'undefined');
}

/**
 * Get RPC configuration summary for logging
 * @returns Configuration summary
 */
export function getRpcConfig(): {
  primary: string;
  heliusAvailable: boolean;
  isClient: boolean;
} {
  return {
    primary: RPC_PRIMARY,
    heliusAvailable: isHeliusAvailable(),
    isClient: typeof window !== 'undefined',
  };
}

